import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaStar } from '@react-icons/all-files/fa/FaStar';
import { FaUmbrellaBeach } from '@react-icons/all-files/fa/FaUmbrellaBeach';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import Breadcrumb from '../../../../../commons/Breadcrumb';
import PageHeader from '../../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../../../commons/Confirm';
import { EditButton, DeleteButton, RenderActions } from '../../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../../commons/Datagrid';
import DateFormat from '../../../../../commons/componentes/DateFormat';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { getEntities, deleteEntity, reset } from '../../../../../manager/dia-festivo.reducer';

const BASE = '/configuracion/calendario/feriados/dias-festivos';
const FERIADOS = '/configuracion/calendario/feriados';

export default function DiasFestivosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.diaFestivo.entities);
  const totalItems = useAppSelector(s => s.diaFestivo.totalItems);
  const loading = useAppSelector(s => s.diaFestivo.loading);
  const updating = useAppSelector(s => s.diaFestivo.updating);
  const updateSuccess = useAppSelector(s => s.diaFestivo.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Día festivo eliminado correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const festivoLabel = (row: any) => {
    const fecha = row.fecha ? String(row.fecha).slice(0, 10) : '';
    const desc = row.descripcion ?? '';
    return fecha && desc ? `${fecha} — ${desc}` : desc || fecha || `#${row.id}`;
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <EditButton onClick={() => navigate(`${BASE}/${params.row.id}/edit`)} />
      <DeleteButton onClick={() => {
        ask(
          <>¿Confirmas que deseas eliminar el día festivo <strong>{festivoLabel(params.row)}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar día festivo', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.5 },
    {
      field: 'fecha',
      headerName: 'Fecha',
      flex: 1,
      renderCell: (p: any) => (
        p.row.fecha
          ? <DateFormat value={p.row.fecha} />
          : <span style={{ color: '#94a3b8' }}>—</span>
      ),
    },
    { field: 'descripcion', headerName: 'Descripción', flex: 3 },
    { field: '__actions', headerName: '', flex: 0.8, renderCell: renderActions },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Calendario', to: '/configuracion/calendario', icon: <FaCalendarAlt /> },
        { label: 'Feriados', to: FERIADOS, icon: <FaUmbrellaBeach /> },
        { label: 'Días Festivos', icon: <FaStar /> },
      ]} />

      <PageHeader
        icon={<FaStar />}
        iconColor="#f59e0b"
        title="Días Festivos"
        subtitle="Catálogo de días festivos oficiales e internos"
        action={{
          label: <><FaPlus style={{ marginRight: 6 }} />Nuevo día festivo</>,
          onClick: () => navigate(`${BASE}/new`),
          color: '#f59e0b',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={[]}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
        defaultSortField="fecha"
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
